import React from 'react'

import { Button, Link } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import { withStyles } from '@material-ui/core/styles'
import { saveAs } from 'file-saver'
import mimeDb from 'mime-db'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import PoziAPIData from 'api/data'
import * as PoziUtils from 'api/utils'
import Downloader from 'drawer/tabs/tools/DownloadMap/Downloader'
import Loading from 'utils/Loading'
import PoziNotifications from 'utils/notifications'

import GetGeoJSON from '../utils/GetGeoJSON'

// .MuiFormLabel-root-66.MuiFormLabel-focused-67
// https://material-ui.com/demos/text-fields/#customized-inputs
const styles = theme => ({
  root: {
    paddingRight: 4
  },
  main: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  select: {},
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: '250px',
    'text-align': 'left',
    'margin-left': 0,
    'padding-left': 0,
    'border-left': 0
  },
  cssLabel: {
    'font-weight': 'normal',
    '&$cssFocused': {
      color: '#424242'
    }
  },
  cssFocused: {},
  link: {},
  download: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px'
  },
  loading: {
    paddingLeft: 10
  }
})

class LayerDownloadPropSelect extends React.Component {
  constructor (props) {
    super(props)
    const { layerPropsDownload } = props

    this.state = {
      downloadStatus: 'idle',
      selectedProp: layerPropsDownload?.prop ?? ''
    }
  }

  componentDidMount () {
    const { outputFormats } = this.props

    console.debug('LayerDownloadPropSelect - componentDidMount - outputFormats', outputFormats)
  }

  downloadStaticGeoJSON = () => {
    const { layerConf } = this.props
    const { title, titleid } = layerConf

    const fileName = `${title}.geojson`
    const geojsonString = GetGeoJSON(titleid)

    console.debug('downloadStaticGeoJSON', titleid, fileName)

    Downloader(geojsonString, fileName)
  }

  /* download()
     This method is currently unused as we use a <Link /> for downloading the content.
     Keeping the code for a bit longer in case we need it again
  */
  download = async () => {
    console.debug('LayerDownloadPropSelect - download')

    const { layerConf } = this.props
    const { selectedProp } = this.state
    const { title } = layerConf

    const url = this.generateDownloadUrl()

    this.setState({ downloadStatus: 'downloading' })

    try {
      await this.fetchUrl(url)

      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: title,
        action: 'download success',
        additionalInfo: selectedProp
      })

      PoziNotifications().notify('Download completed successfully!')

      this.setState({ downloadStatus: 'done' })
    } catch (error) {
      console.log('fetchUrl error', error)

      let errorMsg = String(error)
      if (errorMsg === 'Error: error') {
        errorMsg = `Unexpected error when downloading '${title}'`
      } else {
        errorMsg = `Downloading of '${title}' failed: ${errorMsg}. Please try the alternative download link.`
      }

      PoziUtils.gtag({
        category: 'useraction',
        status: 'warning',
        identifier: title,
        action: 'download error',
        additionalInfo: selectedProp
      })

      PoziNotifications().warning(errorMsg)

      this.setState({ downloadStatus: 'error' })
    }
  }

  fetchUrl = async (url) => {
    const fileName = this.generateFileName()

    console.debug('LayerDownloadPropSelect - fetchUrl - fileName / url', fileName, url)

    let response

    try {
      // await PoziUtils.sleep(5000) // for debugging

      // throw new Error('Oopsie!')

      response = await PoziAPIData().ajax({
        dataType: 'binary',
        url,
        timeout: 1000 * 180, // 180 seconds,
        xhrFields: {
          responseType: 'blob'
        },
        success: () => {
          console.debug('Fetching success')
        },
        error: (request) => {
          console.debug('Fetching error', request)
          // throw new Error(request.statusText)
        }
      }).catch(request => {
        console.debug('Fetching unexpected error', request)
        throw new Error(request.statusText)
      })
    } catch (error) {
      console.debug('Fetch error', error)
      throw error
    }

    // Handle non-streamed download of Blob format
    if (response) {
      console.debug('LayerDownloadPropSelect - fetchUrl - response received. Sending to client...', typeof response, response)
      saveAs(response, fileName)
      console.debug('LayerDownloadPropSelect - fetchUrl - download complete', typeof response, response)
    } else {
      console.debug('LayerDownloadPropSelect - fetchUrl - empty response', typeof response, response)
    }
  }

  generateDownloadUrl = () => {
    const { layerConf, outputFormats } = this.props
    const { selectedProp } = this.state

    const { titleid } = layerConf

    // Update URL to contain the selected output format
    const outputFormat = outputFormats[selectedProp] || selectedProp
    const downloadUrl = PoziUtils.getLayerUrl(layerConf, { OUTPUTFORMAT: outputFormat }, false)

    console.debug('LayerDownloadPropSelect - generateDownloadUrl - url', titleid, downloadUrl, outputFormats)

    return downloadUrl
  }

  generateFileName = () => {
    const { layerConf, outputFormats } = this.props
    const { selectedProp } = this.state

    const outputFormat = outputFormats[selectedProp] || selectedProp

    // Update URL to contain the selected output format
    const url = PoziUtils.getLayerUrl(layerConf, { OUTPUTFORMAT: outputFormat }, false)

    console.debug('LayerDownloadPropSelect - generateFileName', selectedProp, outputFormat, url)

    const mimeDbEntry = mimeDb[outputFormat]
    console.debug('LayerDownloadPropSelect - generateFileName - mime-db entry', mimeDbEntry)

    let fileExtension

    // Try to get the file extension from the 'mime-db' database
    fileExtension = mimeDbEntry?.extensions?.[0]

    // If that fails, construct the extension from the output format and its description
    if (!fileExtension) {
      console.debug('LayerDownloadPropSelect - generateFileName - selectedProp, outputFormat', selectedProp, outputFormat)

      const firstWord = selectedProp.match(/[\w-+]+/g)?.[0] || '' // take first word from the output format description
      const firstWordAsExtension = firstWord.replace(/\W+/g, '.') // replace any non-word characters with a dot
      fileExtension = firstWordAsExtension.toLowerCase() // and make it all lower case
    }

    const fileName = `${layerConf.title}.${fileExtension}`

    console.debug('LayerDownloadPropSelect - generateFileName - file name', fileName)

    return fileName
  }

  handleChange = (event) => {
    const { name, value } = event.target

    this.setState({
      downloadStatus: 'idle',
      [name]: value
    }) // set selectedProp

    console.debug('handleChange download', { ...event })
  }

  onLinkClick = () => {
    const { layerConf } = this.props
    const { selectedProp } = this.state
    const { title } = layerConf

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: title,
      action: 'download link',
      additionalInfo: selectedProp
    })

    this.setState({
      downloadStatus: 'link_clicked'
    })
  }

  render () {
    const { classes, titleid, layerConf, layerSelected, layerVisible, selectValues, outputFormats } = this.props
    const { downloadStatus } = this.state

    if (!layerSelected && !layerVisible) {
      console.log('render - layer not selected/visible. Not rendering.', titleid)
      return null
    }

    console.debug('LayerDownloadPropSelect - render - outputFormats', outputFormats)

    if (!outputFormats) {
      console.log('LayerDownloadPropSelect - render - outputFormats does not exist (yet).', titleid)

      return (
        <div>Nothing to show</div>
      )
    }

    if (!selectValues || selectValues.length === 0 || !titleid) {
      console.log('render - No selectValues, outputFormats or titleid. Not rendering.', titleid, selectValues, outputFormats)

      const downloadGeoJSONButton = (
        <Button className={classes.download}
          size="small"
          variant="outlined"
          onClick={this.downloadStaticGeoJSON}>
            Download GeoJSON
        </Button>)

      return (
        downloadGeoJSONButton
      )
    }

    const menuItemArr = []
    for (const [index, selectLabel] of selectValues.entries()) {
      const key = 'layers-download-select-options-' + titleid + '-' + index

      // const selectValue = outputFormats?.[selectLabel] || selectLabel
      menuItemArr.push(
        <MenuItem key={key} value={selectLabel}>
          <em>{selectLabel}</em>
        </MenuItem>
      )
    }

    const selectId = 'layers-download-select-options-' + titleid

    const outputFormat = this.state.selectedProp
    const downloadUrl = this.generateDownloadUrl()

    const downloadInProgress = downloadStatus === 'downloading'
    const downloadFailed = downloadStatus === 'error'
    const downloadComplete = downloadStatus === 'done'
    const downloadLinkClicked = downloadStatus === 'link_clicked'

    const showDownloadButton = !!outputFormat
    const downloadButtonDisabled = downloadComplete || downloadInProgress || downloadFailed || downloadLinkClicked

    console.debug('Download layerConf outputFormats', layerConf.titleid, layerConf)

    return (
      <div className="pozi-layer-download-prop-select">
        <div className={classes.root}>
          <form className={classes.main} noValidate autoComplete="off">
            <FormControl className={classes.formControl}>
              <InputLabel
                shrink
                classes={{
                  root: classes.cssLabel,
                  focused: classes.cssFocused
                }}
                className={classes.inputLabel}
                htmlFor={selectId}
              >
                Select download format...
              </InputLabel>
              <Select
                className={classes.select}
                value={this.state.selectedProp}
                onChange={this.handleChange}
                id={selectId}
                displayEmpty
                inputProps={{
                  name: 'selectedProp',
                  id: selectId
                }}
              >
                {menuItemArr}
              </Select>
            </FormControl>
          </form>
          <div className={classes.download}>
            {showDownloadButton &&
              <Button className={classes.download}
                disabled={downloadButtonDisabled}
                size="small"
                variant="outlined"
                onClick={this.download}
              >
                  Download
              </Button>
            }

            {downloadFailed &&
            <Link target="_blank" rel="noopener noreferrer" className={classes.link} href={downloadUrl}
              onClick={this.onLinkClick}
              title="Link opens in new tab/window"
            >
              Alternative download
            </Link>
            }

            { downloadInProgress &&
              <Loading text="" />
            }
          </div>
        </div>
      </div>
    )
  }
}

LayerDownloadPropSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  titleid: PropTypes.string.isRequired,
  layerAttributes: PropTypes.object,
  layerConf: PropTypes.object.isRequired,
  layerPropsDownload: PropTypes.object,
  selectValues: PropTypes.array,
  outputFormats: PropTypes.object,
  layerSelected: PropTypes.bool.isRequired,
  layerVisible: PropTypes.bool.isRequired
}

function mapStateToProps (state, ownProps) {
  const layerConf = state.layers?.[ownProps?.titleid]
  const layerPropsDownload = layerConf?.propsDownload ?? null
  const layerAttributes = layerConf?.attributes
  const layerSelected = layerConf?.selected ?? null
  const layerVisible = layerConf?.visible ?? null

  return {
    layerAttributes,
    layerConf,
    layerPropsDownload,
    layerSelected,
    layerVisible
  }
}

const mapDispatchToProps = dispatch => {
  return {}
}

export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LayerDownloadPropSelect)
)
